import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Card,
    CardContent,
    IconButton,
    CircularProgress,
    Alert,
} from "@mui/material";
import {
    PlayArrow as PlayIcon,
    VolumeUp as SpeakerIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import appConfig from "../config.json";

const AiVoice = () => {
    const { t } = useTranslation();
    const [voiceLogs, setVoiceLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [playingLogId, setPlayingLogId] = useState(null);
    const [audioError, setAudioError] = useState(null);
    const [audioLoading, setAudioLoading] = useState(null);

    // Audio element ref
    const audioRef = useRef(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Refs for infinite scroll
    const containerRef = useRef(null);
    const observerRef = useRef(null);

    // Get users data from Redux store
    const userdata = useSelector((state) => state.usersdata);

    // Generate dummy duration for voice logs
    const generateDummyDuration = () => {
        const minutes = Math.floor(Math.random() * 5) + 1; // 1-5 minutes
        const seconds = Math.floor(Math.random() * 60); // 0-59 seconds
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    // Generate dummy VL ID
    const generateDummyId = (index) => {
        return `VL${(index + 1).toString().padStart(3, "0")}`;
    };

    // Normalize phone numbers for matching
    const normalizePhone = (phone) => {
        if (!phone) return "";
        return phone.toString().replace(/^\+?0*/, "");
    };

    // Find user by phone number with flexible matching
    const findUserByPhone = (phoneNumber) => {
        if (!phoneNumber || !userdata.users) return null;

        return userdata.users.find((user) => {
            if (!user.mobile) return false;

            const userPhone = normalizePhone(user.mobile);
            const logPhone = normalizePhone(phoneNumber);

            return userPhone.includes(logPhone) || logPhone.includes(userPhone);
        });
    };

    // Fetch voice logs from API
    const fetchVoiceLogs = async (page = 1, isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                setLoading(true);
                setError(null);
            } else {
                setLoadingMore(true);
            }

            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/audio?page=${page}`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();
            console.log("Voice logs from API:", apiData);

            // Filter and enrich logs with user data
            const enrichedLogs = [];
            let logIndex = isLoadMore ? voiceLogs.length : 0;

            if (apiData.data && Array.isArray(apiData.data)) {
                for (const log of apiData.data) {
                    // Find user by phone number (if phone number exists)
                    const user = log.number_user
                        ? findUserByPhone(log.number_user)
                        : null;

                    // Transform transcript data
                    const transformedTranscription = log.transcript
                        .filter(
                            (item) =>
                                item.message &&
                                item.message.trim() !== "" &&
                                item.message !== "..."
                        )
                        .map((item) => ({
                            speaker: item.role === "user" ? "user" : "ai",
                            text: item.message,
                            timestamp: `${Math.floor(
                                item.time_in_call_secs / 60
                            )}:${(item.time_in_call_secs % 60)
                                .toString()
                                .padStart(2, "0")}`,
                        }));

                    // Always add the log, with "-" for missing user data
                    enrichedLogs.push({
                        id: generateDummyId(logIndex),
                        originalId: log.id,
                        customerName: user
                            ? `${user.firstName || ""} ${
                                  user.lastName || ""
                              }`.trim() || "Unknown User"
                            : "-",
                        customerEmail: user ? user.email || "No email" : "-",
                        customerPhone: log.number_user || "-",
                        date: log.dateUtc,
                        duration: generateDummyDuration(),
                        transcription: transformedTranscription,
                        userId: user ? user.id : null,
                        hasUser: !!user,
                    });

                    logIndex++;
                }
            }

            console.log("Enriched voice logs:", enrichedLogs);

            if (isLoadMore) {
                setVoiceLogs((prev) => [...prev, ...enrichedLogs]);
            } else {
                setVoiceLogs(enrichedLogs);
            }

            // Update pagination state
            setCurrentPage(apiData.current_page || page);
            setTotalPages(apiData.total_pages || 1);
            setHasMore(page < (apiData.total_pages || 1));
        } catch (err) {
            console.error("Error fetching voice logs:", err);
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchVoiceLogs(1, false);
    }, []);

    // Refresh data when user data becomes available
    useEffect(() => {
        if (
            userdata.users &&
            userdata.users.length > 0 &&
            voiceLogs.length > 0
        ) {
            // Re-fetch to enrich existing logs with user data
            fetchVoiceLogs(1, false);
        }
    }, [userdata.users]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
            }
        };
    }, []);

    // Infinite scroll functionality
    const loadMoreLogs = useCallback(() => {
        if (hasMore && !loadingMore && currentPage < totalPages) {
            fetchVoiceLogs(currentPage + 1, true);
        }
    }, [hasMore, loadingMore, currentPage, totalPages]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    loadMoreLogs();
                }
            },
            { threshold: 0.1 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [loadMoreLogs, hasMore, loadingMore]);

    const handleViewTranscription = (log) => {
        setSelectedLog(log);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedLog(null);
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        setHasMore(true);
        fetchVoiceLogs(1, false);
    };

    const handlePlayAudio = async (log) => {
        try {
            // Stop any currently playing audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            // If clicking the same log that's playing, stop it
            if (playingLogId === log.id) {
                setPlayingLogId(null);
                setAudioError(null);
                return;
            }

            setAudioLoading(log.id);
            setAudioError(null);
            setPlayingLogId(null);

            console.log("Fetching audio for log:", log.originalId);

            // Fetch audio file from API
            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/audio/file/${log.originalId}`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Get audio blob
            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);

            // Create and configure audio element
            if (audioRef.current) {
                audioRef.current.src = audioUrl;
                audioRef.current.onloadeddata = () => {
                    setAudioLoading(null);
                    setPlayingLogId(log.id);
                };

                audioRef.current.onended = () => {
                    setPlayingLogId(null);
                    URL.revokeObjectURL(audioUrl); // Clean up blob URL
                };

                audioRef.current.onerror = () => {
                    setAudioLoading(null);
                    setPlayingLogId(null);
                    setAudioError(log.id);
                    URL.revokeObjectURL(audioUrl); // Clean up blob URL
                };

                // Start playing
                await audioRef.current.play();
            }
        } catch (err) {
            console.error("Error playing audio:", err);
            setAudioLoading(null);
            setPlayingLogId(null);
            setAudioError(log.id);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Hidden audio element for playback */}
            <audio ref={audioRef} style={{ display: "none" }} />

            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
                    {t("ai_voice_logs")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {t("ai_voice_description")}
                </Typography>
            </Box>

            {/* Voice Logs Card */}
            <Card sx={{ mb: 3 }} ref={containerRef}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6">
                            {t("call_logs")} ({voiceLogs.length})
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            {loading
                                ? t("loading") || "Loading..."
                                : t("refresh") || "Refresh"}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Error loading voice logs: {error}
                            <Button
                                size="small"
                                onClick={handleRefresh}
                                sx={{ ml: 2 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {audioError && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Failed to load audio file. The audio may not be
                            available for this conversation.
                            <Button
                                size="small"
                                onClick={() => setAudioError(null)}
                                sx={{ ml: 2 }}
                            >
                                Dismiss
                            </Button>
                        </Alert>
                    )}

                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : voiceLogs.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography>{t("no_voice_logs_found")}</Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {voiceLogs.map((log) => (
                                <Card
                                    key={log.id}
                                    variant="outlined"
                                    sx={{
                                        "&:hover": {
                                            boxShadow: 2,
                                            cursor: "pointer",
                                        },
                                    }}
                                >
                                    <CardContent sx={{ py: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: "100%",
                                            }}
                                        >
                                            {/* Left side - Customer info, details and date */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 3,
                                                    flex: 1,
                                                }}
                                            >
                                                {/* Customer Info */}
                                                <Box>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            color: log.hasUser
                                                                ? "inherit"
                                                                : "text.secondary",
                                                            fontStyle:
                                                                log.hasUser
                                                                    ? "normal"
                                                                    : "italic",
                                                        }}
                                                    >
                                                        {log.customerName}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{
                                                            fontStyle:
                                                                log.hasUser
                                                                    ? "normal"
                                                                    : "italic",
                                                        }}
                                                    >
                                                        {log.customerEmail}
                                                    </Typography>
                                                </Box>

                                                {/* Log ID and Duration */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{
                                                            fontFamily:
                                                                "monospace",
                                                            backgroundColor:
                                                                log.hasUser
                                                                    ? "#e3f2fd"
                                                                    : "#f5f5f5",
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            border: log.hasUser
                                                                ? "1px solid #2196f3"
                                                                : "1px solid #e0e0e0",
                                                        }}
                                                    >
                                                        #{log.id}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t("duration")}{" "}
                                                        {log.duration}
                                                    </Typography>
                                                    {!log.hasUser && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                backgroundColor:
                                                                    "#fff3e0",
                                                                color: "#f57c00",
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: 1,
                                                                fontSize:
                                                                    "0.7rem",
                                                            }}
                                                        >
                                                            No User Data
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Date */}
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {new Date(
                                                        log.date
                                                    ).toLocaleDateString(
                                                        "es-ES"
                                                    )}{" "}
                                                    {new Date(
                                                        log.date
                                                    ).toLocaleTimeString(
                                                        "es-ES",
                                                        {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        }
                                                    )}
                                                </Typography>
                                            </Box>

                                            {/* Right side - Action Buttons only */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        audioLoading ===
                                                        log.id ? (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        ) : playingLogId ===
                                                          log.id ? (
                                                            <SpeakerIcon />
                                                        ) : (
                                                            <PlayIcon />
                                                        )
                                                    }
                                                    onClick={() =>
                                                        handlePlayAudio(log)
                                                    }
                                                    sx={{
                                                        minWidth: "auto",
                                                        whiteSpace: "nowrap",
                                                        color:
                                                            audioError ===
                                                            log.id
                                                                ? "error.main"
                                                                : "inherit",
                                                        borderColor:
                                                            audioError ===
                                                            log.id
                                                                ? "error.main"
                                                                : "inherit",
                                                    }}
                                                    disabled={
                                                        audioLoading === log.id
                                                    }
                                                >
                                                    {audioLoading === log.id
                                                        ? t("loading") ||
                                                          "Loading..."
                                                        : playingLogId ===
                                                          log.id
                                                        ? t("playing") ||
                                                          "Playing"
                                                        : audioError === log.id
                                                        ? "Error"
                                                        : t("listen") ||
                                                          "Listen"}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        <VisibilityIcon />
                                                    }
                                                    onClick={() =>
                                                        handleViewTranscription(
                                                            log
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: "auto",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {t("view_transcription")}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}

                            {/* Infinite scroll trigger */}
                            <div ref={observerRef} style={{ height: "20px" }} />

                            {/* Loading more indicator */}
                            {loadingMore && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        py: 2,
                                    }}
                                >
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" sx={{ ml: 2 }}>
                                        Loading more voice logs...
                                    </Typography>
                                </Box>
                            )}

                            {/* End of data indicator */}
                            {!hasMore && voiceLogs.length > 0 && (
                                <Box sx={{ textAlign: "center", py: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No more voice logs to load
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Transcription Detail Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6">
                            {t("conversation")} - {selectedLog?.customerName} (#
                            {selectedLog?.id})
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 1 }}
                    >
                        Date:{" "}
                        {selectedLog?.date
                            ? new Date(selectedLog.date).toLocaleString("es-ES")
                            : "N/A"}{" "}
                        | {t("duration")} {selectedLog?.duration}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Phone:</strong> {selectedLog?.customerPhone}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Email:</strong> {selectedLog?.customerEmail}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Original ID:</strong>{" "}
                            {selectedLog?.originalId}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            fontFamily: '"Courier New", Courier, monospace',
                            fontSize: "15px",
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                            backgroundColor: "#f8f9fa",
                            padding: 2,
                            borderRadius: 1,
                            border: "1px solid #e9ecef",
                        }}
                    >
                        {selectedLog?.transcription.map((item, index) => (
                            <Box key={index} sx={{ mb: 1.5 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontWeight: "bold",
                                        color: "#495057",
                                    }}
                                >
                                    {item.speaker === "user"
                                        ? selectedLog.customerName
                                        : "AI"}
                                    :
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{ ml: 1, color: "#212529" }}
                                >
                                    {item.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AiVoice;
